import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';
import ShareDrawer from '../../components/ShareDrawer';
import CssTransition from '../../components/Transition/CssTransition';
import AnswerButton from '../../components/Assessment/AnswerButton';

import LocationChangePopup from '../../components/Volunteer/LocationChangePopup';
import { AutoScalingButtonSet } from '../../components/Buttons/AutoScalingButtonSet';

import {
    fetchVolunteerOpportunities,
    onInitiateVolunteerAction,
    onShowLocationChangePopup
} from '../Volunteer/actions';

import { isLongText } from '../../components/Shared/commonFunctions';
import { Container } from '../DefaultStyles/Details';
import {
    Body,
    Description,
    Content,
    Details,
    DetailsContainer,
    Footer,
    Title
} from '../DefaultStyles/Shared';
import { setOverlayPartnerVisible } from '../Widget/actions';

import { Placeholders } from '../../utils/fillDynamicText';
import { replaceLocale } from '../../utils/volunteer/helpers';

class VolunteerStart extends React.Component {
    componentDidMount = () => {
        if (!this.props.postalCode) {
            setTimeout(() => {
                this.fetchOpportunitiesOnLoad();
            }, 200);
        } else if (
            !this.props.volunteerOpportunities &&
            this.props.postalCode !== undefined
        ) {
            this.fetchOpportunitiesOnLoad();
        }
    };

    fetchOpportunitiesOnLoad = () => {
        this.props.fetchVolunteerOpportunities(
            this.props.postalCode,
            this.props.countryCode,
            this.props.associatedVolunteerId,
            this.props.actionIdentifier,
            true, // on preload screen
            false,
            true // CACHE ONLY BOOL
        );
    };

    fetchOpportunitesThenAdvance = () => {
        this.props.onInitiateVolunteerAction(
            this.props.actionIdentifier,
            this.props.actionInitiated,
            false
        );
        this.props.fetchVolunteerOpportunities(
            this.props.postalCode,
            this.props.countryCode,
            this.props.associatedVolunteerId,
            this.props.actionIdentifier,
            true, // on preload screen
            false,
            false
        );
    };

    render() {
        return (
            <Container>
                <Overlay />
                <OverlayPublisher />
                <OverlayPartner transitionType="right-to-left" />
                <Body>
                    <WidgetHeader />
                    <Content
                        isContactAction={false}
                        useLocalRepresentative={false}
                        isSingleAction={true}
                    >
                        <Title
                            isLongText={isLongText(this.props.title, 60)}
                            isExtraLongText={isLongText(this.props.title, 90)}
                            onClick={e => {
                                e.preventDefault();
                                this.props.setOverlayPartnerVisible(true, e, false);
                            }}
                            dangerouslySetInnerHTML={{
                                __html: this.props.title
                            }}
                        />
                        <DetailsContainer>
                            <Details>
                                <Description
                                    onClick={e => {
                                        e.preventDefault();
                                        this.props.setOverlayPartnerVisible(
                                            true,
                                            e,
                                            false
                                        );
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: this.props.text
                                    }}
                                />
                            </Details>
                        </DetailsContainer>
                    </Content>
                    <AutoScalingButtonSet mobileStyled={true} mobileLeftPosition={8}>
                        <AnswerButton
                            text={this.props.volunteerButtonText}
                            useLoadingSpinner={false}
                            onClick={() => {
                                this.props.postalCode
                                    ? this.fetchOpportunitesThenAdvance()
                                    : this.props.onShowLocationChangePopup(
                                          true,
                                          this.props.actionIdentifier,
                                          this.props.actionInitiated
                                      );
                            }}
                        />
                    </AutoScalingButtonSet>
                    <Footer>
                        <ShareDrawer backgroundColor={'#fff'} />
                    </Footer>
                </Body>
                <TransitionGroup>
                    {this.props.postalCodeFormPopupIsVisible && (
                        <CssTransition
                            key="alert"
                            in={this.props.postalCodeFormPopupIsVisible}
                        >
                            <LocationChangePopup
                                onBack={() => {
                                    this.props.onShowLocationChangePopup(
                                        false,
                                        this.props.actionIdentifier,
                                        this.props.actionInitiated
                                    );
                                }}
                            />
                        </CssTransition>
                    )}
                </TransitionGroup>
            </Container>
        );
    }
}

VolunteerStart.propTypes = {
    actionIdentifier: PropTypes.string.isRequired,
    actionInitiated: PropTypes.bool.isRequired,
    associatedVolunteerId: PropTypes.number.isRequired,
    fetchVolunteerOpportunities: PropTypes.func.isRequired,
    onInitiateVolunteerAction: PropTypes.func.isRequired,
    onShowLocationChangePopup: PropTypes.func.isRequired,
    postalCode: PropTypes.string,
    countryCode: PropTypes.string,
    postalCodeFormPopupIsVisible: PropTypes.bool.isRequired,
    volunteerButtonText: PropTypes.string.isRequired,
    setOverlayPartnerVisible: PropTypes.func.isRequired,
    text: PropTypes.string,
    title: PropTypes.string,
    volunteerOpportunities: PropTypes.array
};

export default connect(
    state => {
        const defaultTitle = `Find opportunities to volunteer in ${(
            state.widget.endUserLocation || {}
        ).regionName || 'your area'}!`;
        const defaultText = `There are thousands of organizations, groups, and causes in need of volunteers throughout the United States.`;
        const defaultButtonText = `SEE OPPORTUNITIES`;

        return {
            actionIdentifier: state.widget.activeAction.identifier,
            actionInitiated: state.volunteer.actionInitiated,
            associatedVolunteerId: state.widget.activeAction.id,
            postalCode: state.widget.endUserLocation
                ? state.widget.endUserLocation.postalCode
                : undefined,
            countryCode: state.widget.endUserLocation
                ? state.widget.endUserLocation.countryCode
                : 'US',
            postalCodeFormPopupIsVisible: state.volunteer.postalCodeFormPopupIsVisible,
            volunteerButtonText:
                state.widget.activeAction.customButtonText || defaultButtonText,
            text: (state.widget.activeAction.text || defaultText).replace(
                new RegExp(Placeholders.LocalePlaceholder, 'g'),
                replaceLocale(state)
            ),
            title: (state.widget.activeAction.title || defaultTitle).replace(
                new RegExp(Placeholders.LocalePlaceholder, 'g'),
                replaceLocale(state)
            ),
            volunteerOpportunities: state.volunteer.volunteerOpportunities
        };
    },
    {
        setOverlayPartnerVisible,
        fetchVolunteerOpportunities,
        onInitiateVolunteerAction,
        onShowLocationChangePopup
    }
)(VolunteerStart);
